import React, { useState } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { paymentsApi } from "../services/payments";
import type { InstantDestination } from "../types/payments";
import "./AddDebitCardSheet.css";
import { t } from "../i18n";
export type { InstantDestination } from "../types/payments";

// Instant Payouts — the in-app debit card form (founder: "go,
// build the in-app card form"). Stripe's own card field renders here; the
// number is tokenised by Stripe.js on the device and never reaches our
// server. We post the token, the server attaches it to the talent's Stripe
// account (lib/payouts/instantCard.js — instant-capable or detached again),
// and answers with the refreshed status so the caller can flip to
// "Instant ✓" on the spot. About 20 seconds, no leaving the app.
//
// Same publishable key + secure-context guard as the fan checkout
// (FanLiveCard). Without either, `canAddCardInApp` is false and callers fall
// back to the Stripe dashboard link.

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost";
const stripePromise = STRIPE_PK && isSecure ? loadStripe(STRIPE_PK) : null;

export const canAddCardInApp = !!stripePromise;

interface Props {
  /** The Stripe account's payout currency — the token must be created in it. */
  currency: string;
  onAdded: (destination: InstantDestination, instant: any) => void;
  onClose: () => void;
}

// Dark booth: white digits, muted placeholder, red only when Stripe says so.
const CARD_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#f3f4f6",
      fontSize: "16px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', system-ui, sans-serif",
      "::placeholder": { color: "#6b7280" },
      iconColor: "#9ca3af",
    },
    invalid: { color: "#f87171", iconColor: "#f87171" },
  },
};

function CardForm({ currency, onAdded, onClose }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    setBusy(true);
    setErr(null);
    try {
      const { token, error } = await stripe.createToken(card, { currency });
      if (error || !token) {
        setErr(error?.message || t("addDebitCardSheet.checkTheCardAnd"));
        return;
      }
      const data = await paymentsApi.addInstantCard(token.id);
      onAdded(data.destination, data.instant);
    } catch (e: any) {
      setErr(e?.response?.data?.msg || t("addDebitCardSheet.couldnTAddThe"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="cardsheet" onSubmit={submit}>
      <h3 className="cardsheet-title">{t("addDebitCardSheet.addADebitCard")}</h3>
      <p className="cardsheet-sub">{t("addDebitCardSheet.moneyLandsInMin")}</p>
      <div className={`cardsheet-field${err ? " has-error" : ""}`}>
        <CardElement
          options={CARD_OPTIONS}
          onReady={() => setReady(true)}
          onChange={(ev) => setErr(ev.error?.message || null)}
        />
      </div>
      {err && (
        <p className="cardsheet-err" role="alert">
          {err}
        </p>
      )}
      <div className="cardsheet-actions">
        <button type="button" className="cardsheet-cancel" onClick={onClose} disabled={busy}>
          {t("addDebitCardSheet.cancel")}
        </button>
        <button type="submit" className="cardsheet-add" disabled={!stripe || !ready || busy}>
          {busy ? t("addDebitCardSheet.adding") : t("addDebitCardSheet.addCard")}
        </button>
      </div>
      <p className="cardsheet-note">{t("addDebitCardSheet.securedByStripeWe")}</p>
    </form>
  );
}

export default function AddDebitCardSheet(props: Props) {
  if (!stripePromise) return null;
  return createPortal(
    <div
      className="cardsheet-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="cardsheet-panel" role="dialog" aria-modal="true" aria-label={t("addDebitCardSheet.addADebitCard")}>
        <Elements stripe={stripePromise} options={{ locale: "en" }}>
          <CardForm {...props} />
        </Elements>
      </div>
    </div>,
    document.body
  );
}
